import type { JSX } from "react";
import { useMemo, useState } from "react";
import { Pie, PieChart } from "recharts";
import type { AllocationDimension, PortfolioAllocation } from "@finance-os/shared-types/api";
import { ALLOCATION_DIMENSIONS, type AllocationDimensionConst } from "@finance-os/shared-types/constants";

import { collapseSlices } from "../../lib/portfolio-slices.js";
import { usePortfolioFormat } from "../../lib/use-portfolio-format.js";
import { cn } from "../../lib/utils.js";
import { Money } from "../Money.js";
import { Segmented } from "../Segmented.js";
import {
  CHART_PALETTE,
  chartSeries,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "../ui/chart.js";
import { PanelCard, type PanelQuery } from "./PanelCard.js";

// Keyed by the shared dimension list, so a new dimension can't ship without a label.
const DIMENSION_LABELS: Record<AllocationDimension, string> = {
  asset_class: "Asset class",
  currency: "Currency",
  broker: "Broker",
  account: "Account",
};

const DIMENSION_OPTIONS = ALLOCATION_DIMENSIONS.map((value) => ({ value, label: DIMENSION_LABELS[value] }));

/** Holdings and cash as a donut with a legend, sliced by a switchable dimension. */
export function AllocationPanel({ query }: { query: PanelQuery<PortfolioAllocation> }): JSX.Element {
  const fmt = usePortfolioFormat();
  const [dimension, setDimension] = useState<AllocationDimensionConst>(ALLOCATION_DIMENSIONS[0]);

  const slices = useMemo(
    () => collapseSlices(query.data?.by_dimension[dimension] ?? [], CHART_PALETTE.length),
    [query.data, dimension],
  );
  const pieData = slices.map((s, i) => ({ ...s, fill: chartSeries(i).color }));
  const config: ChartConfig = Object.fromEntries(
    slices.map((s, i) => [s.key, { label: s.label, color: chartSeries(i).color }]),
  );

  return (
    <PanelCard
      title="Allocation"
      action={<Segmented label="Group by" value={dimension} options={DIMENSION_OPTIONS} onChange={setDimension} />}
      isLoading={query.isLoading}
      isError={query.isError}
      empty={slices.length === 0 ? "No positions or cash yet — allocation appears after the next broker sync." : null}
    >
      <div className="flex flex-col items-center gap-4">
        <ChartContainer config={config} className="aspect-square h-48 shrink-0">
          <PieChart>
            <ChartTooltip
              content={<ChartTooltipContent hideLabel nameKey="key" valueFormatter={(v) => fmt.money(v)} />}
            />
            <Pie data={pieData} dataKey="value_eur_minor" nameKey="key" innerRadius={52} strokeWidth={2} />
          </PieChart>
        </ChartContainer>
        <ul className="w-full min-w-0 flex-1 space-y-2 text-sm">
          {slices.map((s, i) => (
            <li key={s.key} className="flex items-center gap-2">
              <span className={cn("size-2.5 shrink-0 rounded-[2px]", chartSeries(i).swatch)} />
              <span className="min-w-0 flex-1 truncate">{s.label}</span>
              <span className="text-muted-foreground w-14 shrink-0 text-right tabular-nums">{fmt.pct(s.weight_pct)}</span>
              <span className="w-24 shrink-0 text-right font-medium tabular-nums">
                <Money amountMinor={s.value_eur_minor} native="EUR" />
              </span>
            </li>
          ))}
        </ul>
      </div>
    </PanelCard>
  );
}
